import React, { useState } from "react";
import { Package, ChevronDown, ChevronUp } from "lucide-react";

interface CargoItem {
  quantity?: number;
  type?: string;
  weightKg?: number;
  volumeM3?: number;
  length?: number;
  width?: number;
  height?: number;
  ldm?: number;
  description?: string;
  marks?: string;
}

interface Props {
  cargoItems?: CargoItem[];
  referenceNumber?: string;
}

function fmt(n?: number, decimals = 0) {
  if (n == null || n === 0) return null;
  return decimals > 0 ? n.toFixed(decimals) : String(n);
}

function hasDimensions(item: CargoItem) {
  return (item.length ?? 0) > 0 || (item.width ?? 0) > 0 || (item.height ?? 0) > 0;
}

const TYPE_LABELS: Record<string, string> = {
  EUR: "EUR Pallet", BOX: "Box", PLT: "Pallet", CTN: "Carton",
  BAG: "Bag", DRM: "Drum", CYL: "Cylinder", CRATE: "Crate",
  FTL: "FTL", LTL: "LTL", OTHER: "Other",
};

const Pill: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex flex-col min-w-0">
    <span className="text-[10px] uppercase tracking-wide text-gray-400 font-medium">{label}</span>
    <span className="text-sm font-semibold text-gray-800 truncate">{value}</span>
  </div>
);

const POHCargoDetails: React.FC<Props> = ({ cargoItems, referenceNumber }) => {
  const [open, setOpen] = useState(true);

  if (!cargoItems?.length) return null;

  const totalQty    = cargoItems.reduce((s, i) => s + (i.quantity ?? 0), 0);
  const totalWeight = cargoItems.reduce((s, i) => s + (i.weightKg ?? 0), 0);
  const totalLdm    = cargoItems.reduce((s, i) => s + (i.ldm ?? 0), 0);
  const totalVol    = cargoItems.reduce((s, i) => s + (i.volumeM3 ?? 0), 0);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">

      {/* ── Header ── */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200 hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Package className="h-4 w-4 text-purple-600" />
          <span className="text-sm font-semibold text-gray-900">Cargo Information</span>
          <span className="text-xs font-medium text-gray-500 bg-gray-200 px-1.5 py-0.5 rounded-full">
            {cargoItems.length} line{cargoItems.length > 1 ? "s" : ""}
          </span>
        </div>
        <div className="flex items-center gap-3">
          {referenceNumber && (
            <span className="text-xs text-gray-400 font-mono hidden sm:block">{referenceNumber}</span>
          )}
          {open ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
        </div>
      </button>

      {open && (
        <>
          {/* ── Summary totals ── */}
          <div className="grid grid-cols-4 divide-x divide-gray-100 border-b border-gray-100 bg-gray-50/50">
            {[
              { label: "Total Qty",   value: totalQty    > 0 ? String(totalQty)            : "—" },
              { label: "Weight",      value: totalWeight > 0 ? `${totalWeight.toFixed(1)} kg` : "—" },
              { label: "LDM",         value: totalLdm    > 0 ? totalLdm.toFixed(2)           : "—" },
              { label: "Volume",      value: totalVol    > 0 ? `${totalVol.toFixed(2)} m³`   : "—" },
            ].map(({ label, value }) => (
              <div key={label} className="flex flex-col items-center py-2.5 px-2 text-center">
                <span className="text-[10px] uppercase tracking-wide text-gray-400 font-medium">{label}</span>
                <span className="text-sm font-bold text-gray-800 mt-0.5">{value}</span>
              </div>
            ))}
          </div>

          {/* ── Line items ── */}
          <div className="divide-y divide-gray-100">
            {cargoItems.map((item, i) => (
              <div key={i} className="px-4 py-3 space-y-2">

                {/* Row 1: index + qty×type + weight + volume */}
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="w-5 h-5 rounded-full bg-gray-100 text-gray-500 text-[10px] font-bold flex items-center justify-center shrink-0">
                    {i + 1}
                  </span>

                  {/* Qty × Type */}
                  <div className="flex items-center gap-1.5">
                    {item.quantity != null && (
                      <span className="text-sm font-bold text-gray-900">{item.quantity}×</span>
                    )}
                    {item.type && (
                      <span className="text-xs font-semibold px-2 py-0.5 bg-purple-50 text-purple-700 rounded border border-purple-200">
                        {TYPE_LABELS[item.type] ?? item.type}
                      </span>
                    )}
                  </div>

                  {/* Metrics in pills */}
                  <div className="flex items-center gap-4 ml-auto flex-wrap">
                    {fmt(item.weightKg) && (
                      <Pill label="Weight" value={`${item.weightKg} kg`} />
                    )}
                    {fmt(item.volumeM3) && (
                      <Pill label="Volume" value={`${item.volumeM3} m³`} />
                    )}
                    {fmt(item.ldm) && (
                      <Pill label="LDM" value={String(item.ldm)} />
                    )}
                    {hasDimensions(item) && (
                      <Pill
                        label="L × W × H"
                        value={`${item.length ?? 0} × ${item.width ?? 0} × ${item.height ?? 0}`}
                      />
                    )}
                  </div>
                </div>

                {/* Row 2: description + marks */}
                {(item.description || item.marks) && (
                  <div className="ml-8 flex flex-wrap gap-x-6 gap-y-1">
                    {item.description && (
                      <p className="text-xs text-gray-600">
                        <span className="text-gray-400 font-medium">Desc: </span>
                        {item.description}
                      </p>
                    )}
                    {item.marks && (
                      <p className="text-xs text-gray-500 font-mono">
                        <span className="text-gray-400 font-medium not-italic">Marks: </span>
                        {item.marks}
                      </p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default POHCargoDetails;
